import { Rectangle } from 'pixi.js';
import type { CanvasTextMetrics } from '../metrics/CanvasTextMetrics';
import type { DynamicHeight, DynamicWidth } from '../../../enums';
import {
    DynamicInferredDimensions,
    DynamicInferredHeight,
    DynamicInferredWidth,
    DynamicLevel,
    DynamicXAlign,
    DynamicYAlign
} from '../../../enums';
import { isBreakingSpace } from '../textMetricsHelpers';
import type { CanvasTextDynamicGraphicRendererSettingsBase } from './CanvasTextDynamicGraphic';
import { CanvasTextDynamicGraphicRenderer } from './CanvasTextDynamicGraphic';
import type { CanvasTextDynamicBoxRendererSettings } from './CanvasTextDynamicBox';
import type { CanvasTextDynamicBoxBorderRendererSettings } from './CanvasTextDynamicBoxBorder';


export interface CanvasTextDynamicBoxBaseRendererSettings extends CanvasTextDynamicGraphicRendererSettingsBase {
    boxHeight: DynamicHeight;
    boxWidth: DynamicWidth;
}

export type Padding = {
    top: number;
    bottom: number;
    left: number;
    right: number;
};

/**
 * CanvasTextDynamicBox is a class that renders a rounded rectangle behind a block of text.
 */
export abstract class CanvasTextDynamicBoxBaseRenderer extends CanvasTextDynamicGraphicRenderer {
    boxHeight: DynamicHeight = DynamicInferredHeight.height;
    boxWidth: DynamicWidth = DynamicInferredWidth.width;

    /**
     * This contains the bounds representation for each shape that this graphic will render. The bounds here
     * result from the level property. An object level graphic will always just have one bounds, but a line
     * level graphic will have one bounds for each line of text, etc. These bounds represent what will be drawn
     * and have their origin relative to the top left corner of the text block.
     */
    protected _levelBounds: Rectangle[];

    protected abstract _getPadding(): Padding;

    #applyPaddingToBounds(levelBounds: Rectangle[]) {
        const padding = this._getPadding();
        levelBounds.forEach(bounds => {
            applyPaddingToRectangle(bounds, padding);
        });
    }

    override apply(settings: CanvasTextDynamicBoxRendererSettings | CanvasTextDynamicBoxBorderRendererSettings) {
        super.apply(settings);
        this.boxHeight = settings.boxHeight;
        this.boxWidth = settings.boxWidth;
    }

    /**
     * Builds up a collection of bounds, one for each level element. Once the bounds are generated we can use
     * them to draw the individual shapes and calculate the combined bounds.
     */
    override build(metrics: CanvasTextMetrics) {
        super.build(metrics);
        let levelBounds: Rectangle[];

        switch (this.level) {
            case DynamicLevel.object: levelBounds = this.#getObjectBounds(metrics); break;
            case DynamicLevel.line: levelBounds = this.#getLineBounds(metrics); break;
            case DynamicLevel.word: levelBounds = this.#getWordBounds(metrics); break;
            case DynamicLevel.highlight: levelBounds = this.#getHighlightBounds(metrics); break;
            default: throw new Error(`Invalid level ${this.level}`);
        }

        this.#applyPaddingToBounds(levelBounds);
        this._levelBounds = levelBounds;
    }

    #getObjectBounds(metrics: CanvasTextMetrics) {
        const { x, width } = this.#getWidthAndX(metrics.bounds, metrics.bounds);
        const { y, height } = this.#getHeightAndY(metrics, metrics.bounds, metrics.bounds, metrics.baselineY);
        const rectangle = new Rectangle(x, y, width, height);
        return [rectangle];
    }

    #getLineBounds(metrics: CanvasTextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line) => {
            const { x, width } = this.#getWidthAndX(line.bounds, metrics.bounds);
            // Also use the line for max height bounds, because we want to limit the graphic on each line to the line height
            const { y, height } = this.#getHeightAndY(metrics, line.bounds, line.bounds, line.baselineY);

            const rectangle = new Rectangle(x, y, width, height);
            bounds.push(rectangle);
        });
        return bounds;
    }

    #getWordBounds(metrics: CanvasTextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    return;
                }

                // Also use the word for max width bounds, because we want to limit the graphic on each word to the word width
                const { x, width } = this.#getWidthAndX(word.bounds, word.bounds, bounds.at(-1));
                const { y, height } = this.#getHeightAndY(metrics, word.bounds, line.bounds, line.baselineY);

                const rectangle = new Rectangle(x, y, width, height);
                bounds.push(rectangle);
            });
        });

        return bounds;
    }

    #getHighlightBounds(metrics: CanvasTextMetrics) {
        const bounds: Rectangle[] = [];
        metrics.lines.forEach((line) => {
            line.highlightSpans.forEach((span) => {
                // Also use the span for max width bounds, because we want to limit the graphic on each span to the span width
                const { x, width } = this.#getWidthAndX(span.bounds, span.bounds, bounds.at(-1));
                const { y, height } = this.#getHeightAndY(metrics, span.bounds, line.bounds, line.baselineY);

                const rectangle = new Rectangle(x, y, width, height);
                bounds.push(rectangle);
            });
        });

        return bounds;
    }

    #getWidthAndX(levelBounds: Rectangle, maxWidthBounds: Rectangle, previousBounds?: Rectangle): { x: number; width: number; } {
        let x: number, width: number;
        if (typeof this.boxWidth === 'number') {
            width = this.boxWidth;
            switch (this.alignX) {
                case DynamicXAlign.left: x = levelBounds.x; break;
                case DynamicXAlign.center: x = levelBounds.x + (levelBounds.width * 0.5) - (this.boxWidth * 0.5); break;
                case DynamicXAlign.right: x = levelBounds.x + levelBounds.width - this.boxWidth; break;
                default: throw new Error(`Invalid alignX ${this.alignX}`);
            }
        }
        else {
            switch (this.boxWidth) {
                case DynamicInferredWidth.width:
                    x = levelBounds.x;
                    width = levelBounds.width;
                    break;
                case DynamicInferredWidth.maxWidth:
                    x = maxWidthBounds.x;
                    width = maxWidthBounds.width;
                    break;
                case DynamicInferredWidth.gaplessWidth:
                    previousBounds = previousBounds ?? levelBounds;
                    const gap = gapSize(previousBounds.x + previousBounds.width, levelBounds.x);
                    x = levelBounds.x - gap;
                    width = levelBounds.width + gap;
                    break;
                default:
                    throw new Error(`Invalid inferred box width ${this.boxWidth}`);
            }
        }
        return {
            x: x + this.positionX,
            width,
        };
    }

    #getHeightAndY(metrics: CanvasTextMetrics, levelBounds: Rectangle, maxHeightBounds: Rectangle, baselineY: number): { y: number; height: number; } {
        let y: number, height: number;
        if (typeof this.boxHeight === 'number') {
            height = this.boxHeight;
            switch (this.alignY) {
                case DynamicYAlign.maxTop: y = maxHeightBounds.y; break;
                case DynamicYAlign.top: y = levelBounds.y; break;
                case DynamicYAlign.center: y = levelBounds.y + (levelBounds.height * 0.5) - (this.boxHeight * 0.5); break;
                case DynamicYAlign.averageCenter: y = maxHeightBounds.y + (maxHeightBounds.height * 0.5) - (this.boxHeight * 0.5); break;
                case DynamicYAlign.baseline: y = baselineY - this.boxHeight; break;
                case DynamicYAlign.bottom: y = levelBounds.y + levelBounds.height - this.boxHeight; break;
                case DynamicYAlign.maxBottom: y = maxHeightBounds.y + maxHeightBounds.height - this.boxHeight; break;
                default: throw new Error(`Invalid alignY ${this.alignY}`);
            }
        }
        else if (this.level === DynamicLevel.object) {
            y = levelBounds.y;
            height = levelBounds.height;
        }
        else {
            const { maxLineDescent, maxLineAscent } = metrics;
            const maxHeight = maxLineAscent + maxLineDescent;
            const baselineFactor = metrics.lines.length !== 0
                ? metrics.baseLineOffsetY / maxHeight
                : 0;

            switch (this.boxHeight) {
                case DynamicInferredHeight.height:
                    y = levelBounds.y;
                    height = levelBounds.height;
                    break;
                case DynamicInferredHeight.maxHeight:
                    y = baselineY - maxLineAscent;
                    height = maxHeight;
                    break;
                case DynamicInferredHeight.heightWithoutDescent:
                    y = baselineY - maxLineAscent;
                    height = maxLineAscent;
                    break;
                case DynamicInferredHeight.gapless:
                    y = baselineY - (maxLineAscent * baselineFactor);
                    height = metrics.baseLineOffsetY;
                    break;
                default:
                    throw new Error(`Invalid inferred box height ${this.boxHeight}`);
            }
        }

        return {
            y: y + this.positionY,
            height,
        };
    }

    override _getInferredValues(inferredDimension: DynamicInferredDimensions): number[] {
        switch (inferredDimension) {
            case DynamicInferredDimensions.height:
                return this._levelBounds.map(bounds => bounds.height);
            case DynamicInferredDimensions.maxHeight:
                const maxHeight = Math.max(...this._levelBounds.map(bounds => bounds.height));
                return Array(this._levelBounds.length).fill(maxHeight);
            case DynamicInferredDimensions.width:
                return this._levelBounds.map(bounds => bounds.width);
            case DynamicInferredDimensions.maxWidth:
                const maxWidth = Math.max(...this._levelBounds.map(bounds => bounds.width));
                return Array(this._levelBounds.length).fill(maxWidth);

            // The rest are not valid inferred dimensions when animating a graphic
            case DynamicInferredDimensions.gapless:
            case DynamicInferredDimensions.heightWithoutDescent:
            case DynamicInferredDimensions.gaplessWidth:
            default:
                throw new Error(`Invalid inferred dimension ${inferredDimension}`);
        }
    }
}

/**
 * Calculates the gap size to fill when rendering `gaplessWidth` word graphics.
 * @param x - The x coordinate of the edge on the gap.
 * @param prevSiblingClosestEdge - If left-to-right text, this is x + width, the closest edge to the x coord.
 * @returns The gap, or zero if the gap appears to be negative, which implies the `prevSiblingClosestEdge` is on
 * the previous line.
 */
const gapSize = (prevSiblingClosestEdge: number, x: number): number => {
    const gapSize = x - prevSiblingClosestEdge;

    // If < 0, this implies the previousSiblingCLosestEdge is on the previous line, so this
    // is a new line, and there is no gap.
    return gapSize > 0 ? gapSize : 0;
};

/**
 * Applies padding to a rectangle by updating the passed rectangle in place.
 */
const applyPaddingToRectangle = (rectangle: Rectangle, padding: Padding) => {
    rectangle.x -= padding.left;
    rectangle.y -= padding.top;
    rectangle.width += padding.left + padding.right;
    rectangle.height += padding.top + padding.bottom;
};

